import { useEffect, useRef } from 'react';
import { Box, Button, Typography } from '@mui/material';

// Keep active content out of the editable DOM. The saved value changes only on user input.
const safeMarkup = (html) => {
  const document = new DOMParser().parseFromString(html || '', 'text/html');
  document.querySelectorAll('script,style,iframe,object,embed,link,meta,svg,math,form,input,button,video,audio,source,img').forEach((node) => node.remove());
  const allowedTags = new Set('p div span h1 h2 h3 h4 h5 h6 table thead tbody tfoot tr td th strong b em i u s strike sub sup br hr ul ol li blockquote pre code a'.split(' '));
  [...document.body.querySelectorAll('*')].reverse().forEach((node) => {
    if (!allowedTags.has(node.tagName.toLowerCase())) {
      node.replaceWith(...node.childNodes);
      return;
    }
    [...node.attributes].forEach(({ name, value }) => {
      if (!['href', 'title', 'colspan', 'rowspan'].includes(name) || (name === 'href' && !/^(https?:\/\/|mailto:)/i.test(value))) node.removeAttribute(name);
    });
  });
  return document.body.innerHTML;
};

export default function TemplateBodyEditor({ label, value, onChange, disabled, variables }) {
  const editor = useRef(null);
  useEffect(() => {
    if (editor.current && document.activeElement !== editor.current) editor.current.innerHTML = safeMarkup(value);
  }, [value]);
  const command = (name, text) => {
    editor.current.focus();
    document.execCommand(name, false, text);
    onChange(editor.current.innerHTML);
  };
  return <Box>
    <Typography sx={{ mb: 1 }}>{label}</Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
      {[['Bold', 'bold'], ['Italic', 'italic'], ['Underline', 'underline'], ['Bulleted list', 'insertUnorderedList']].map(([title, action]) => <Button key={action} size="small" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => command(action)}>{title}</Button>)}
      {variables.map((variable) => <Button key={variable} size="small" disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => command('insertText', variable)}>{variable}</Button>)}
    </Box>
    <Box ref={editor} role="textbox" aria-label={label} aria-multiline="true" aria-disabled={disabled} contentEditable={!disabled} suppressContentEditableWarning
      onPaste={(event) => {
        event.preventDefault();
        const html = event.clipboardData.getData('text/html');
        command(html ? 'insertHTML' : 'insertText', html ? safeMarkup(html) : event.clipboardData.getData('text/plain'));
      }}
      onDrop={(event) => event.preventDefault()}
      onInput={() => onChange(editor.current.innerHTML)} sx={{ minHeight: 160, p: 2, border: '1px solid #cbd5e1', borderRadius: 1, bgcolor: disabled ? 'action.disabledBackground' : 'background.paper', overflowWrap: 'anywhere' }} />
  </Box>;
}
